import React, { useState } from "react";
import axios from "axios";
import {
  Users, Loader2, AlertTriangle, ThumbsUp, ThumbsDown, Minus,
  Clock, MessageSquare, Zap, Copy, Check, Phone
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { API } from "../App";
import { toast } from "sonner";

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const MOOD_OPTIONS = [
  { value: "enthusiastic", label: "Enthusiastic", icon: ThumbsUp, color: "text-green-600 bg-green-50 border-green-200" },
  { value: "neutral", label: "Neutral", icon: Minus, color: "text-slate-600 bg-slate-50 border-slate-200" },
  { value: "hesitant", label: "Hesitant", icon: ThumbsDown, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { value: "negative", label: "Negative", icon: ThumbsDown, color: "text-red-600 bg-red-50 border-red-200" },
];

export default function MeetingDebrief({ leadId, leadName, leadProject, open, onClose, onComplete, visitIdToComplete, initialMeetingType }) {
  const [step, setStep] = useState("form"); // "form" | "result"
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [meetingType, setMeetingType] = useState(initialMeetingType || "");

  const [form, setForm] = useState({
    interest_level: "medium",
    buyer_mood: "neutral",
    questions_asked: "",
    objections_raised: "",
    units_viewed: "",
    next_steps_discussed: "",
    notes: "",
  });

  // Reset meetingType when dialog opens with new props
  React.useEffect(() => {
    if (open) {
      if (visitIdToComplete) setMeetingType("visit_complete");
      else if (initialMeetingType) setMeetingType(initialMeetingType);
      else setMeetingType("");
    }
  }, [open, visitIdToComplete, initialMeetingType]);

  const isVisitCompletion = !!visitIdToComplete;
  const isMandatory = isVisitCompletion || meetingType === "walkin_revisit";

  const MEETING_TYPES = [
    { value: "visit_complete", label: "Site Visit Completed", hidden: true },
    { value: "walkin", label: "Walk-in (First Visit)" },
    { value: "walkin_revisit", label: "Walk-in Revisit" },
    { value: "negotiation", label: "Negotiation Meeting" },
    { value: "office_meeting", label: "Office Meeting" },
    { value: "other", label: "Other Face-to-Face" },
  ];

  const resetAndClose = () => {
    if (isMandatory && step === "form") {
      toast.error("Please complete the debrief form first");
      return;
    }
    setStep("form");
    setResult(null);
    setMeetingType("");
    setForm({
      interest_level: "medium",
      buyer_mood: "neutral",
      questions_asked: "",
      objections_raised: "",
      units_viewed: "",
      next_steps_discussed: "",
      notes: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.notes.trim()) {
      toast.error("Please add meeting notes");
      return;
    }
    if (!meetingType && !visitIdToComplete) {
      toast.error("Please select a meeting type");
      return;
    }
    setLoading(true);
    try {
      const effectiveType = visitIdToComplete ? "visit_complete" : meetingType;

      // Step 1: Create the right backend record based on meeting type
      if (effectiveType === "visit_complete") {
        await axios.put(`${API}/visits/${visitIdToComplete}`, { status: "completed" });
      } else if (effectiveType === "walkin" || effectiveType === "walkin_revisit") {
        await axios.post(`${API}/visits`, {
          lead_id: leadId,
          scheduled_date: new Date().toISOString(),
          visit_type: effectiveType === "walkin_revisit" ? "revisit" : "first_visit",
          project: leadProject || "",
          status: "completed",
          is_unscheduled: true,
          post_visit_feedback: form.notes,
          interest_level: form.interest_level,
        });
      }
      // For negotiation/office/other — the AI debrief endpoint already logs an interaction

      // Step 2: Run AI debrief analysis
      const payload = {
        interest_level: form.interest_level,
        buyer_mood: form.buyer_mood,
        questions_asked: form.questions_asked ? form.questions_asked.split(",").map(s => s.trim()).filter(Boolean) : [],
        objections_raised: form.objections_raised ? form.objections_raised.split(",").map(s => s.trim()).filter(Boolean) : [],
        units_viewed: form.units_viewed ? form.units_viewed.split(",").map(s => s.trim()).filter(Boolean) : [],
        next_steps_discussed: form.next_steps_discussed,
        notes: form.notes,
      };
      const res = await axios.post(`${API}/ai-screens/post-visit/${leadId}`, payload);
      setResult(res.data);
      setStep("result");

      const labels = { visit_complete: "Visit completed & debrief saved", walkin: "Walk-in logged & debrief saved", walkin_revisit: "Walk-in revisit logged & debrief saved", negotiation: "Negotiation meeting logged", office_meeting: "Office meeting logged", other: "Meeting logged" };
      toast.success(labels[effectiveType] || "Debrief saved");
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save debrief");
    } finally {
      setLoading(false);
    }
  };

  const SCORE_COLORS = {
    hot: "bg-red-100 text-red-700",
    warm: "bg-amber-100 text-amber-700",
    nurture: "bg-blue-100 text-blue-700",
    dormant: "bg-slate-100 text-slate-500",
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetAndClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="meeting-debrief-dialog" onPointerDownOutside={isMandatory && step === "form" ? (e) => e.preventDefault() : undefined} onEscapeKeyDown={isMandatory && step === "form" ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-teal-600" />
            </div>
            {step === "form" ? `Log Meeting — ${leadName}` : `Meeting Intelligence — ${leadName}`}
          </DialogTitle>
          <p className="text-sm text-slate-500">{step === "form" ? "Record your meeting observations for AI analysis" : "AI-generated follow-up insights"}</p>
        </DialogHeader>

        {isMandatory && step === "form" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Debrief is mandatory to mark this visit as complete
          </div>
        )}

        {step === "form" && (
          <div className="space-y-4 mt-2">
            {/* Meeting Type Selector — shown when not triggered from visit completion */}
            {!visitIdToComplete && (
              <div>
                <Label className="text-sm font-medium">Meeting Type *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5">
                  {MEETING_TYPES.filter(t => !t.hidden).map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setMeetingType(type.value)}
                      className={`py-2 px-3 rounded-lg border text-xs font-medium transition-all text-center ${
                        meetingType === type.value
                          ? "bg-teal-100 border-teal-300 text-teal-800"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`meeting-type-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Interest Level */}
            <div>
              <Label className="text-sm font-medium">Interest Level</Label>
              <div className="flex gap-2 mt-1.5">
                {["high", "medium", "low"].map((level) => (
                  <button
                    key={level}
                    onClick={() => setForm(f => ({ ...f, interest_level: level }))}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all capitalize ${
                      form.interest_level === level
                        ? level === "high" ? "bg-green-100 border-green-300 text-green-800" 
                          : level === "medium" ? "bg-amber-100 border-amber-300 text-amber-800"
                          : "bg-red-100 border-red-300 text-red-800"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`interest-${level}`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Buyer Mood */}
            <div>
              <Label className="text-sm font-medium">Buyer Mood</Label>
              <div className="grid grid-cols-4 gap-2 mt-1.5">
                {MOOD_OPTIONS.map((mood) => {
                  const Icon = mood.icon;
                  return (
                    <button
                      key={mood.value}
                      onClick={() => setForm(f => ({ ...f, buyer_mood: mood.value }))}
                      className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-xs font-medium transition-all ${
                        form.buyer_mood === mood.value ? mood.color : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                      }`}
                      data-testid={`mood-${mood.value}`}
                    >
                      <Icon className="w-4 h-4" />
                      {mood.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">Meeting Notes *</Label>
              <Textarea
                placeholder="What happened? Key observations, buyer's body language, what they focused on..."
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="mt-1 min-h-[80px]"
                data-testid="meeting-notes-input"
              />
            </div>

            {/* Questions & Objections */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Questions They Asked</Label>
                <Input
                  placeholder="Comma-separated: price, possession..."
                  value={form.questions_asked}
                  onChange={(e) => setForm(f => ({ ...f, questions_asked: e.target.value }))}
                  className="mt-1"
                  data-testid="questions-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Objections Raised</Label>
                <Input
                  placeholder="Comma-separated: too expensive, far..."
                  value={form.objections_raised}
                  onChange={(e) => setForm(f => ({ ...f, objections_raised: e.target.value }))}
                  className="mt-1"
                  data-testid="objections-input"
                />
              </div>
            </div>

            {/* Units & Next Steps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Units/Flats Viewed</Label>
                <Input
                  placeholder="e.g., A-401, B-302"
                  value={form.units_viewed}
                  onChange={(e) => setForm(f => ({ ...f, units_viewed: e.target.value }))}
                  className="mt-1"
                  data-testid="units-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Next Steps Discussed</Label>
                <Input
                  placeholder="e.g., Will come with family next week"
                  value={form.next_steps_discussed}
                  onChange={(e) => setForm(f => ({ ...f, next_steps_discussed: e.target.value }))}
                  className="mt-1"
                  data-testid="nextsteps-input"
                />
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="submit-debrief-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              {loading ? "Analyzing..." : "Save & Get AI Insights"}
            </Button>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-4 mt-2">
            {/* Score Recommendation */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Badge className={`text-sm px-3 py-1 capitalize ${SCORE_COLORS[result.interpretation?.score_recommendation] || ""}`}>
                {result.interpretation?.score_recommendation}
              </Badge>
              <Badge variant="outline" className="capitalize text-xs">{result.interpretation?.confidence} confidence</Badge>
            </div>

            {/* Analysis */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">AI Analysis</p>
                <CopyBtn text={result.interpretation?.analysis || ""} />
              </div>
              <p className="text-sm text-blue-900">{result.interpretation?.analysis}</p>
            </div>

            {/* Follow-up Timeline */}
            {result.interpretation?.follow_up_timeline?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Clock className="w-3.5 h-3.5 text-indigo-600" />
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Follow-up Plan</p>
                </div>
                <div className="space-y-2">
                  {result.interpretation.follow_up_timeline.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Badge variant="outline" className="shrink-0 text-xs capitalize">{step.timing}</Badge>
                      <div>
                        <p className="text-slate-800">{step.action}</p>
                        <p className="text-xs text-slate-500 capitalize">via {step.channel}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message Drafts */}
            {result.interpretation?.message_drafts && (
              <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-600" /> Ready-to-Send Messages
                </p>
                {result.interpretation.message_drafts.thank_you && (
                  <div className="bg-emerald-50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-emerald-700">Thank You</p>
                      <CopyBtn text={result.interpretation.message_drafts.thank_you} />
                    </div>
                    <p className="text-sm text-emerald-900">{result.interpretation.message_drafts.thank_you}</p>
                  </div>
                )}
                {result.interpretation.message_drafts.follow_up && (
                  <div className="bg-blue-50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-blue-700">Follow-up</p>
                      <CopyBtn text={result.interpretation.message_drafts.follow_up} />
                    </div>
                    <p className="text-sm text-blue-900">{result.interpretation.message_drafts.follow_up}</p>
                  </div>
                )}
              </div>
            )}

            {/* Buying Signals & Risks side-by-side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {result.interpretation?.buying_signals?.length > 0 && (
                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-green-700 mb-1">Buying Signals</p>
                  <ul className="space-y-1">
                    {result.interpretation.buying_signals.map((s, i) => (
                      <li key={i} className="text-sm text-green-800 flex items-start gap-1.5">
                        <ThumbsUp className="w-3 h-3 mt-1 shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.interpretation?.risk_factors?.length > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">Risk Factors</p>
                  <ul className="space-y-1">
                    {result.interpretation.risk_factors.map((r, i) => (
                      <li key={i} className="text-sm text-red-800 flex items-start gap-1.5">
                        <AlertTriangle className="w-3 h-3 mt-1 shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Next Best Action */}
            {result.interpretation?.next_best_action && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-xs font-semibold text-amber-700 mb-1">Next Best Action</p>
                <p className="text-sm font-medium text-amber-900">{result.interpretation.next_best_action}</p>
              </div>
            )}

            <Button variant="outline" onClick={resetAndClose} className="w-full" data-testid="close-debrief-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
